import { _objectEntries, _uniqBy } from '@naturalcycles/js-lib'
import { LocaleObject, locales } from '@src/cnst/locales.cnst'
import { SupportedLocale } from '@src/cnst/translations.cnst'
import type { ReactNode } from 'react'

type LocaleToPathAlternateLinksProps = BaseAlternateLinksProps & {
  /**
   * Record of locales to path for cases where the path differs with the locale
   * Ex. [Locales.esUS]: 'cyclematters/some-slug' }
   * If this field is used, the path field and the includedLocales field will be ignored
   */
  localeToPath: Partial<Record<SupportedLocale, string>>
}

type PathAlternateLinksProps = BaseAlternateLinksProps & {
  /**
   * The path of the page, used to generate the canonical URL
   * It's assumed to be used in the format https://naturalcycles.com/{locale}/{slug}
   *
   * Ex. For the path "plan-pregnancy" and locale "es" or "en-US" the canonical URLs would be:
   *
   * | Locale |                    URL                      |
   * | ------ | ------------------------------------------- |
   * | es-US  | https://naturalcycles.com/es/plan-pregnancy |
   * | en-US  | https://naturalcycles.com/plan-pregnancy    |
   */
  path: string
  /**
   * Locales to include in the alternate links. If empty, all locales will be included.
   */
  includedLocales?: SupportedLocale[]
}

export type AlternateLinksProps = PathAlternateLinksProps | LocaleToPathAlternateLinksProps
interface AltLink {
  href: string
  localeObj?: LocaleObject
}

interface Links {
  alternates: AltLink[]
  xDefaultLocale?: string
}

interface BaseAlternateLinksProps {
  /**
   * Duplicates specific locale alternate links to other locales. Useful for, for example, es-US -> es-MX
   */
  extraLocales?: Partial<Record<SupportedLocale, `${string}-${string}`>>
}

/**
 * Adds alternate links to the head of the document for the provided locales.
 * To be used in the head export of a Gatsby template.
 *
 * You can either provide a path and optional includedLocales, or a localeToPath object.
 * localeToPath allows for more granular control over the paths for each locale,
 * while path and includedLocales are more convenient for simple cases.
 */
export function AlternateLinks(props: AlternateLinksProps): ReactNode {
  const { alternates, xDefaultLocale } = isPathAlternateLinksProps(props)
    ? handlePathAlternateLinksProps(props)
    : handleLocaleToPathAlternateLinksProps(props)

  return mapAlternatesToLinks(alternates, xDefaultLocale)
}

export function mapAlternatesToLinks(alternates: AltLink[], xDefaultLocale?: string): ReactNode[] {
  return _uniqBy(alternates, alt => alt.localeObj?.locale)
    .map(({ href, localeObj }) => (
      <link
        key={`${localeObj?.locale}-alternate`}
        rel="alternate"
        href={href}
        id={`alternate-${localeObj?.locale}`}
        hrefLang={getHrefLang(localeObj)}
      />
    ))
    .concat(
      xDefaultLocale ? (
        <link
          key="alternate-x-default"
          rel="alternate"
          hrefLang="x-default"
          id="alternate-x-default"
          href={alternates.find(a => a.localeObj?.locale === xDefaultLocale)?.href}
        />
      ) : (
        []
      ),
    )
}

function isPathAlternateLinksProps(props: AlternateLinksProps): props is PathAlternateLinksProps {
  return (props as PathAlternateLinksProps).path !== undefined
}

function stripSlash(str: string): string {
  const trailingSlashRegex = /\/+$/
  const startSlashRegex = /^\//
  return str.replace(trailingSlashRegex, '').replace(startSlashRegex, '')
}

/*
 * If somebody includes slashes in their AlternateLinks path, we should remove them
 */
function getFullPath(localeUrl: string | undefined, slug: string): string {
  return `https://www.naturalcycles.com${localeUrl ? `/${localeUrl}` : ''}${slug ? `/${stripSlash(slug)}` : ''}`
}

/**
 * Generates alternate links from extraLocales
 */
function getAlternatesFromExtraLocales(
  extraLocales: Partial<Record<SupportedLocale, `${string}-${string}`>>,
  existingAlternates: readonly AltLink[],
): AltLink[] {
  const alternates: AltLink[] = []
  const toToAlternate = _objectEntries(extraLocales).map(([to, from]) => {
    const toAlt = existingAlternates.find(l => l.localeObj?.locale === to)
    return { from, toAlt }
  })
  toToAlternate.forEach(({ from, toAlt }) => {
    if (toAlt) {
      alternates.push({
        href: toAlt.href,
        localeObj: {
          locale: from,
        } as LocaleObject,
      })
    }
  })
  return alternates
}

export function handlePathAlternateLinksProps(props: PathAlternateLinksProps): Links {
  const {
    includedLocales = locales.map(l => l.locale).filter(locale => locale !== SupportedLocale.enGB),
    path,
    extraLocales,
  } = props
  /* If we have includedLocales, and this page doesn't exist in en-US(doesn't exist right now),
   * we should set x-default to the first locale in the includedLocales array. Otherwise,
   * we should set x-default to en-US */
  const xDefaultLocale =
    !includedLocales || includedLocales.includes(SupportedLocale.enUS)
      ? SupportedLocale.enUS
      : includedLocales[0]

  const alternates: AltLink[] = includedLocales.map(locale => {
    const localeObj = locales.find(l => l.locale === locale)
    return {
      href: getFullPath(localeObj?.url, path),
      localeObj,
    }
  })

  if (extraLocales) {
    alternates.push(...getAlternatesFromExtraLocales(extraLocales, alternates))
  }

  return { alternates, xDefaultLocale }
}

export function handleLocaleToPathAlternateLinksProps(
  props: LocaleToPathAlternateLinksProps,
): Links {
  const { localeToPath, extraLocales } = props

  const alternates: AltLink[] = _objectEntries(localeToPath).map(([locale, path]) => {
    const localeObj = locales.find(l => l.locale === locale)
    return {
      href: getFullPath(undefined, path!),
      localeObj,
    }
  })

  if (extraLocales) {
    alternates.push(...getAlternatesFromExtraLocales(extraLocales, alternates))
  }

  const xDefaultLocale = Object.keys(localeToPath).includes(SupportedLocale.enUS)
    ? SupportedLocale.enUS
    : Object.keys(localeToPath)[0]

  return { alternates, xDefaultLocale }
}

/**
 * We generally only want to show the language in the hreflang tag, unless it's en-CA
 * because en already exists from the en-US locale
 */
function getHrefLang(localeObj?: LocaleObject): string {
  if (!localeObj) {
    return SupportedLocale.enUS
  }
  if (![SupportedLocale.enCA, SupportedLocale.enGB].includes(localeObj.locale)) {
    return localeObj?.language
  }
  return localeObj?.locale
}
